/*
 * Privacy Policy. Content describes the data flows this site actually has:
 * the contact form, Umami analytics, Google Fonts, the Google Maps embed, and
 * Railway server logs. Update this page when those change.
 */
import LegalLayout, { Section, P, Sub, Bullets, Callout } from "../components/LegalLayout";

const LAST_UPDATED = "July 31, 2026";

export default function Privacy() {
  return (
    <LegalLayout
      kicker="PRIVACY POLICY"
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      documentTitle="Privacy Policy | Blue Collar AI"
      summary="How Blue Collar AI, Inc. collects, uses, and protects information when you visit this website or get in touch with us."
    >
      <Section heading="1. Who We Are">
        <P>
          Blue Collar AI, Inc. ("Blue Collar AI," "we," "us," or "our") is an artificial
          intelligence consulting firm and brand holding company headquartered in Columbus,
          Ohio, serving clients worldwide. This Privacy Policy explains how we handle
          information in connection with our website at www.bluecollarai.online (the "Site").
        </P>
        <P>
          If you do not agree with this policy, please do not use the Site.
        </P>
      </Section>

      <Section heading="2. Information We Collect">
        <Sub>Information you give us</Sub>
        <P>
          When you contact us through the Site's contact form, by email, or by phone, we
          receive whatever you choose to send. Through the contact form that typically means:
        </P>
        <Bullets
          items={[
            "Your name",
            "Your email address",
            "Your business name",
            "The contents of your message",
          ]}
        />
        <P>
          If you email or call us directly, we receive your email address or phone number
          along with the contents of your message. Please do not send us sensitive personal
          information — such as Social Security numbers, financial account numbers, or health
          information — through the Site or by unencrypted email.
        </P>

        <Sub>Information collected automatically</Sub>
        <P>
          Like most websites, our hosting provider records standard server logs when you
          visit. These may include your IP address, browser type and version, operating
          system, the pages you request, referring URLs, and the date and time of your visit.
          We use these logs to operate the Site, diagnose problems, and protect against abuse.
        </P>
        <P>
          We also use Umami, a privacy-focused analytics tool, to understand how the Site is
          used in aggregate — for example, which pages are viewed most often. Umami does not
          use cookies, does not track you across other websites, and does not collect
          information that identifies you personally.
        </P>

        <Sub>Local storage</Sub>
        <P>
          The Site may store a display preference (such as your light or dark theme choice)
          in your browser's local storage. This stays on your device, is not transmitted to
          us, and can be cleared through your browser settings at any time.
        </P>
      </Section>

      <Section heading="3. Cookies and Similar Technologies">
        <P>
          Blue Collar AI does not set advertising or cross-site tracking cookies on this Site,
          and we do not run behavioral advertising. Third-party content embedded in the Site —
          notably the Google Maps embed on our contact section — may set its own cookies or
          similar technologies when it loads. Those are governed by the third party's own
          privacy policy, described in the next section.
        </P>
        <P>
          Most browsers let you block or delete cookies through their settings. Blocking
          third-party cookies may affect how embedded content such as the map displays, but
          the rest of the Site will continue to work.
        </P>
      </Section>

      <Section heading="4. Third-Party Services">
        <P>
          We rely on a small number of third-party providers to deliver the Site. When their
          content loads in your browser, they may receive your IP address and basic technical
          information about your device:
        </P>
        <Bullets
          items={[
            <>
              <strong className="text-white">Railway</strong> — hosting and infrastructure for
              the Site, including server logs.
            </>,
            <>
              <strong className="text-white">Google Fonts</strong> — serves the typefaces used
              on the Site.
            </>,
            <>
              <strong className="text-white">Google Maps</strong> — powers the embedded map
              showing our service area.
            </>,
            <>
              <strong className="text-white">Umami</strong> — cookieless, aggregate website
              analytics.
            </>,
          ]}
        />
        <P>
          We do not control these providers' practices. We encourage you to review their
          privacy policies directly.
        </P>
      </Section>

      <Section heading="5. How We Use Information">
        <P>We use the information described above to:</P>
        <Bullets
          items={[
            "Respond to your inquiries, questions, and requests for a consultation",
            "Provide, operate, maintain, and improve the Site and our services",
            "Communicate with you about our services, projects, and scheduling",
            "Understand in aggregate how visitors use the Site so we can improve it",
            "Detect, investigate, and prevent fraud, abuse, and security incidents",
            "Comply with legal obligations and enforce our Terms & Conditions",
          ]}
        />
        <P>
          We do not use the information you send us to train artificial intelligence models
          offered to other clients.
        </P>
      </Section>

      <Section heading="6. How We Share Information">
        <Callout>
          We do not sell your personal information, and we do not share it with third parties
          for their own direct marketing purposes.
        </Callout>
        <P>We may share information in these limited circumstances:</P>
        <Bullets
          items={[
            "With service providers who help us operate the Site and our business — such as hosting, email, and analytics providers — who are permitted to use it only to perform services for us",
            "Among Blue Collar AI, Inc. and its affiliated brands, where doing so is necessary to respond to your inquiry",
            "When required by law, subpoena, court order, or other legal process, or to protect the rights, property, or safety of Blue Collar AI, our clients, or the public",
            "In connection with a merger, acquisition, financing, or sale of all or part of our business, in which case we will take reasonable steps to ensure your information remains protected",
          ]}
        />
      </Section>

      <Section heading="7. Data Retention">
        <P>
          We keep inquiry and correspondence records for as long as needed to respond to you,
          to maintain a record of our business relationship, and to satisfy legal, tax, and
          accounting requirements. Server logs and aggregate analytics are retained on a
          rolling basis. When information is no longer needed, we delete it or remove the
          details that identify you.
        </P>
      </Section>

      <Section heading="8. Security">
        <P>
          The Site is served over encrypted HTTPS connections, and we apply reasonable
          administrative and technical safeguards to protect the information in our care.
          No website or method of electronic transmission is completely secure, however, and
          we cannot guarantee absolute security. You send information to us at your own risk.
        </P>
      </Section>

      <Section heading="9. Your Rights and Choices">
        <P>
          Depending on where you live, you may have the right to request access to the
          personal information we hold about you, to ask us to correct or delete it, to
          object to or restrict certain processing, or to request a copy in a portable format.
        </P>
        <P>
          To make a request, email{" "}
          <a
            href="mailto:chris@bluecollarai.online"
            className="text-electric hover:text-white transition-colors underline underline-offset-4"
          >
            chris@bluecollarai.online
          </a>
          . We will respond within the time required by applicable law. We may need to verify
          your identity before acting on a request.
        </P>
        <Sub>California residents</Sub>
        <P>
          Blue Collar AI does not sell or share personal information as those terms are
          defined by the California Consumer Privacy Act, and we have not done so in the
          preceding twelve months. California residents may exercise their rights to know,
          delete, and correct by using the email address above, and we will not discriminate
          against you for doing so.
        </P>
        <Sub>Marketing communications</Sub>
        <P>
          If we send you email about our services, you can opt out at any time by replying
          with a request to unsubscribe. We will still send messages necessary to respond to
          an inquiry or administer an active engagement.
        </P>
      </Section>

      <Section heading="10. Children's Privacy">
        <P>
          The Site is intended for business audiences and is not directed to children under
          16. We do not knowingly collect personal information from children under 16. If you
          believe a child has provided us with personal information, contact us and we will
          delete it.
        </P>
      </Section>

      <Section heading="11. Links to Other Sites">
        <P>
          The Site links to websites operated by our affiliated brands and by third parties.
          Those sites have their own privacy policies and terms, and this Privacy Policy does
          not apply to them. We are not responsible for the content or privacy practices of
          any site we link to. Review the policy of each site you visit.
        </P>
      </Section>

      <Section heading="12. International Visitors">
        <P>
          Blue Collar AI is based in the United States, and the Site is hosted and operated
          there. If you access the Site from outside the United States, you understand that
          your information will be transferred to, stored, and processed in the United States,
          where data protection laws may differ from those in your country.
        </P>
      </Section>

      <Section heading="13. Changes to This Policy">
        <P>
          We may update this Privacy Policy from time to time. When we do, we will revise the
          "Last updated" date at the top of this page. Material changes will be made
          conspicuous on the Site. Your continued use of the Site after an update means you
          accept the revised policy.
        </P>
      </Section>

      <Section heading="14. Contact Us">
        <P>
          Questions about this Privacy Policy or how we handle your information? Get in touch:
        </P>
        <Bullets
          items={[
            <>
              Email:{" "}
              <a
                href="mailto:chris@bluecollarai.online"
                className="text-electric hover:text-white transition-colors underline underline-offset-4"
              >
                chris@bluecollarai.online
              </a>
            </>,
            <>
              Phone:{" "}
              <a
                href="tel:+16146337935"
                className="text-electric hover:text-white transition-colors underline underline-offset-4"
              >
                +1 (614) 633-7935
              </a>
            </>,
            <>Blue Collar AI, Inc. — Columbus, Ohio, United States</>,
          ]}
        />
      </Section>
    </LegalLayout>
  );
}
